import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  STOCK_TAB_QUERY_KEYS,
  stockControlSessionPath,
  stockTabIndexFromQuery,
} from './stockPaths';
import {
  Box,
  Button,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormLabel,
  Textarea,
  useDisclosure,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  TableContainer,
  Tabs,
  TabList,
  Tab,
  Select,
  Checkbox,
  Spinner,
  VStack,
} from '@chakra-ui/react';
import { getData, postData, putData } from '../../../utils/httpClient';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import ProductSearchSelect from '../../../components/stock/ProductSearchSelect';
import { exportStockExcel } from '../../../utils/exportStockExcel';
import { exportStockControlSessionExcel } from '../../../utils/exportStockControlExcel';
import ConfirmModal from '../../../components/modal/ConfirmModal';
import { formatCompactProductCode } from '../../../utils/stockControlUtils';
import authService from '../../../services/authService';

const movementLabel = (m) => {
  if (m.movementType === 'service') {
    return (m.quantity || 0) > 0
      ? { label: 'Devolución servicio', color: 'teal' }
      : { label: 'Consumo servicio', color: 'purple' };
  }
  const map = {
    in: { label: 'Ingreso', color: 'green' },
    out: { label: 'Egreso', color: 'red' },
    adjust: { label: 'Ajuste', color: 'orange' },
  };
  return map[m.movementType] || { label: m.movementType, color: 'gray' };
};

const isControlOnlyMovement = (m) => {
  if (m.movementType !== 'adjust' || m.quantity !== 0) return false;
  const n = (m.notes || '').toLowerCase();
  if (n.includes('zona:') || n.includes('repisa:')) return false;
  return (
    n.includes('control de stock') ||
    n.includes('control físico') ||
    n.includes('conforme')
  );
};

const formatLotExpiration = (raw) => {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1900) return '—';
  return d.toLocaleDateString('es-UY');
};

const lotZoneKey = (lot) => {
  const z = lot?.zone?.trim();
  return z || 'Sin zona';
};

const ReasonModal = ({ isOpen, onClose, title, children, onConfirm, isLoading }) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {children}
          <FormLabel fontSize="sm" mt={3}>
            Motivo / aclaración <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <Textarea
            size="sm"
            rows={3}
            placeholder="Ej: Conteo físico, merma, corrección por error de carga..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            size="sm"
            colorScheme="gray"
            isLoading={isLoading}
            onClick={() => onConfirm(reason)}
            isDisabled={reason.trim().length < 3}
          >
            Confirmar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default function StockAdmin() {
  const canEdit = authService.constructor.checkPermissionFromStorage('products.edit');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [zones, setZones] = useState({});
  const [movements, setMovements] = useState([]);
  const [controlSessions, setControlSessions] = useState([]);
  const [exportData, setExportData] = useState({ zones: {}, movements: [] });
  const [locationFilter, setLocationFilter] = useState('');
  const [movementFilter, setMovementFilter] = useState('');
  const [controlFilter, setControlFilter] = useState('');
  const [discardTarget, setDiscardTarget] = useState(null);
  const [discarding, setDiscarding] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedLot, setSelectedLot] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [actionQty, setActionQty] = useState('');
  const [loading, setLoading] = useState(false);
  const [highlightLotId, setHighlightLotId] = useState(null);
  const [expandedIndices, setExpandedIndices] = useState([]);
  const { isOpen: isNewLotOpen, onOpen: onNewLotOpen, onClose: onNewLotClose } = useDisclosure();
  const {
    isOpen: isFromInvoiceOpen,
    onOpen: onFromInvoiceOpen,
    onClose: onFromInvoiceClose,
  } = useDisclosure();
  const [newLotReason, setNewLotReason] = useState('');
  const [form, setForm] = useState({
    productId: '',
    shelfLetter: '',
    shelfNumber: '',
    zone: '',
    quantity: 0,
    expirationDate: '',
  });
  const [purchaseInvoices, setPurchaseInvoices] = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [invoiceLines, setInvoiceLines] = useState([]);
  const [invoiceLocation, setInvoiceLocation] = useState({
    zone: '',
    shelfLetter: '',
    shelfNumber: '',
    expirationDate: '',
  });
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [savingFromInvoice, setSavingFromInvoice] = useState(false);
  const [forceReloadInvoice, setForceReloadInvoice] = useState(false);
  const [editLot, setEditLot] = useState(null);
  const [editForm, setEditForm] = useState({
    zone: '',
    shelfLetter: '',
    shelfNumber: '',
    expirationDate: '',
    reason: '',
  });
  const toast = useToast();

  useEffect(() => {
    const idx = stockTabIndexFromQuery(searchParams.get('tab'));
    if (idx != null) setTabIndex(idx);
  }, [searchParams]);

  const load = useCallback(async () => {
    try {
      const [stockRes, movRes, sessionsRes] = await Promise.all([
        getData('/stock'),
        getData('/stock/movements'),
        getData('/stock/control-sessions'),
      ]);
      const z = stockRes?.payload?.zones || {};
      const m = movRes?.payload?.movements || [];
      const sessions = sessionsRes?.payload?.sessions || [];
      setZones(z);
      setMovements(m);
      setControlSessions(sessions);
      setExportData({ zones: z, movements: m });
      return z;
    } catch {
      toast({ title: 'Error al cargar inventario', status: 'error', duration: 3000 });
      return null;
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateLot = async () => {
    if (!form.productId) {
      toast({ title: 'Seleccione un producto', status: 'warning', duration: 3000 });
      return;
    }
    if (newLotReason.trim().length < 3) {
      toast({ title: 'Indique el motivo del alta', status: 'warning', duration: 3000 });
      return;
    }
    try {
      const res = await postData('/stock/lots', {
        productId: Number(form.productId),
        quantity: Number(form.quantity) || 0,
        zone: form.zone.trim() || null,
        shelfLetter: form.shelfLetter.trim() || null,
        shelfNumber: form.shelfNumber.trim() || null,
        expirationDate: form.expirationDate || null,
        reason: newLotReason.trim(),
      });
      const created = res?.payload?.lot;
      const zoneKey = lotZoneKey(created || form);
      toast({ title: 'Lote creado', status: 'success', duration: 2500 });
      onNewLotClose();
      setNewLotReason('');
      setLocationFilter('');
      if (created?.id) setHighlightLotId(created.id);
      const freshZones = await load();
      if (freshZones) {
        const zoneNames = Object.keys(freshZones).sort((a, b) => a.localeCompare(b, 'es'));
        const idx = zoneNames.indexOf(zoneKey);
        if (idx >= 0) {
          setExpandedIndices((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
        }
      }
    } catch (e) {
      toast({
        title: e?.message || e?.response?.data?.message || 'Error al crear lote',
        status: 'error',
        duration: 4000,
      });
    }
  };

  const openFromInvoiceModal = async () => {
    setSelectedInvoiceId('');
    setInvoiceLines([]);
    setForceReloadInvoice(false);
    setInvoiceLocation({
      zone: '',
      shelfLetter: '',
      shelfNumber: '',
      expirationDate: '',
    });
    onFromInvoiceOpen();
    try {
      const res = await getData(
        '/provider-invoices?page=1&limit=100&status=Pendiente&sortBy=date&sortDir=desc',
        { skipLoader: true },
      );
      setPurchaseInvoices(res?.payload?.invoices || []);
    } catch {
      setPurchaseInvoices([]);
      toast({
        title: 'No se pudieron cargar las facturas de compra',
        status: 'error',
        duration: 3500,
      });
    }
  };

  const loadInvoiceDetails = async (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setInvoiceLines([]);
    if (!invoiceId) return;
    setLoadingInvoice(true);
    try {
      const res = await getData(
        `/provider-invoices/${encodeURIComponent(invoiceId)}`,
        { skipLoader: true },
      );
      const details = res?.payload?.invoice?.providerInvoiceDetails || [];
      setInvoiceLines(
        details.map((d) => ({
          detailId: d.id,
          productId: d.productId,
          productName: d.productName || d.product?.name || 'Producto',
          productCode: d.product?.code || '',
          quantity: Number(d.quantity) || 0,
          included: Boolean(d.productId) && Number(d.quantity) > 0,
        })),
      );
    } catch (e) {
      toast({
        title: e?.message || 'No se pudo cargar la factura',
        status: 'error',
        duration: 3500,
      });
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleCreateLotsFromInvoice = async () => {
    if (!selectedInvoiceId) {
      toast({ title: 'Seleccione una factura', status: 'warning', duration: 3000 });
      return;
    }
    const lines = invoiceLines
      .filter((l) => l.included && l.productId && Number(l.quantity) > 0)
      .map((l) => ({
        detailId: l.detailId,
        productId: Number(l.productId),
        quantity: Number(l.quantity),
      }));
    if (!lines.length) {
      toast({
        title: 'No hay productos válidos para cargar',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setSavingFromInvoice(true);
    try {
      const res = await postData('/stock/lots/from-invoice', {
        invoiceId: selectedInvoiceId,
        zone: invoiceLocation.zone.trim() || null,
        shelfLetter: invoiceLocation.shelfLetter.trim() || null,
        shelfNumber: invoiceLocation.shelfNumber.trim() || null,
        expirationDate: invoiceLocation.expirationDate || null,
        force: forceReloadInvoice,
        lines,
      });
      const count = res?.payload?.lots?.length || 0;
      toast({
        title: `${count} lote(s) cargados desde la factura`,
        status: 'success',
        duration: 3000,
      });
      onFromInvoiceClose();
      await load();
    } catch (e) {
      const msg =
        e?.message ||
        e?.response?.data?.message ||
        'Error al cargar lotes desde la factura';
      toast({
        title: msg,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingFromInvoice(false);
    }
  };

  const runLotAction = async (reason) => {
    if (!selectedLot || !actionModal) return;
    setLoading(true);
    try {
      const qty = Number(actionQty);
      const body = { quantity: qty, reason: reason.trim() };

      if (actionModal === 'add') {
        await postData(`/stock/lots/${selectedLot.id}/add`, body);
        toast({ title: 'Stock ingresado', status: 'success', duration: 2500 });
      } else if (actionModal === 'deduct') {
        await postData(`/stock/lots/${selectedLot.id}/deduct`, body);
        toast({ title: 'Stock descontado', status: 'success', duration: 2500 });
      } else if (actionModal === 'adjust') {
        await postData(`/stock/lots/${selectedLot.id}/adjust`, body);
        toast({ title: 'Stock ajustado', status: 'success', duration: 2500 });
      }

      setActionModal(null);
      setSelectedLot(null);
      load();
    } catch (e) {
      toast({
        title: e?.response?.data?.message || 'Error en movimiento',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const openAction = (lot, type) => {
    setSelectedLot(lot);
    setActionModal(type);
    if (type === 'adjust') setActionQty(String(lot.quantity));
    else setActionQty('');
  };

  const openEditLot = (lot) => {
    setEditLot(lot);
    setEditForm({
      zone: lot.zone || '',
      shelfLetter: lot.shelfLetter || '',
      shelfNumber: lot.shelfNumber || '',
      expirationDate: lot.expirationDate
        ? String(lot.expirationDate).slice(0, 10)
        : '',
      reason: '',
    });
  };

  const confirmEditLot = async () => {
    if (!editLot || editForm.reason.trim().length < 3) {
      toast({ title: 'Indique el motivo de la edición', status: 'warning', duration: 3000 });
      return;
    }
    setLoading(true);
    try {
      await putData(`/stock/lots/${editLot.id}`, {
        zone: editForm.zone,
        shelfLetter: editForm.shelfLetter,
        shelfNumber: editForm.shelfNumber,
        expirationDate: editForm.expirationDate || null,
        reason: editForm.reason.trim(),
      });
      toast({ title: 'Lote actualizado', status: 'success', duration: 2500 });
      setEditLot(null);
      load();
    } catch (e) {
      toast({
        title: e?.response?.data?.message || 'Error al editar lote',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterText = locationFilter.trim().toLowerCase();
  const zoneEntries = useMemo(
    () =>
      Object.entries(zones).sort(([a], [b]) => a.localeCompare(b, 'es')),
    [zones],
  );
  const zoneNamesKey = useMemo(
    () => zoneEntries.map(([name]) => name).join('\0'),
    [zoneEntries],
  );

  useEffect(() => {
    setExpandedIndices(zoneEntries.map((_, i) => i));
  }, [zoneNamesKey, zoneEntries]);

  useEffect(() => {
    if (!highlightLotId) return undefined;
    const t = setTimeout(() => setHighlightLotId(null), 8000);
    return () => clearTimeout(t);
  }, [highlightLotId]);

  const filterLot = (lot) => {
    if (!filterText) return true;
    const hay = [
      lot.product?.name,
      lot.product?.code,
      lot.zone,
      lot.shelfLetter,
      lot.shelfNumber,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(filterText);
  };

  const movementFilterText = movementFilter.trim().toLowerCase();
  const operationalMovements = movements.filter((m) => !isControlOnlyMovement(m));
  const filteredMovements = operationalMovements.filter((m) => {
    if (!movementFilterText) return true;
    const hay = [
      m.product?.name,
      m.product?.code,
      m.notes,
      m.movementType,
      m.user?.name,
      String(m.lotId),
      String(m.vehicleServiceId),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(movementFilterText);
  });

  const visibleLotCount = zoneEntries.reduce(
    (n, [, lots]) => n + lots.filter(filterLot).length,
    0,
  );

  const controlFilterText = controlFilter.trim().toLowerCase();
  const filteredControlSessions = controlSessions.filter((s) => {
    if (!controlFilterText) return true;
    const hay = [
      s.status,
      s.userName,
      String(s.id),
      s.startedAt,
      s.finishedAt,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(controlFilterText);
  });

  const sessionStatusLabel = (status) => {
    if (status === 'finalized') return { label: 'Finalizado', color: 'green' };
    if (status === 'draft') return { label: 'Borrador', color: 'orange' };
    return { label: status, color: 'gray' };
  };

  const handleDiscardSession = (sessionRow) => {
    setDiscardTarget(sessionRow);
  };

  const confirmDiscardSession = async () => {
    if (!discardTarget) return;
    setDiscarding(true);
    try {
      await postData(`/stock/control-sessions/${discardTarget.id}/discard`);
      toast({ title: 'Borrador descartado', status: 'info', duration: 2500 });
      setDiscardTarget(null);
      load();
    } catch {
      toast({ title: 'No se pudo descartar', status: 'error', duration: 3000 });
    } finally {
      setDiscarding(false);
    }
  };

  const actionTitles = {
    add: 'Ingresar stock',
    deduct: 'Descontar stock',
    adjust: 'Ajustar stock (nuevo total)',
  };

  return (
    <AdminPageShell
      title="Inventario"
      actions={
        canEdit ? (
          <>
            <Button
              size="sm"
              variant="adminPrimary"
              mr={2}
              onClick={() => navigate('/admin/stock/control')}
            >
              Control de stock
            </Button>
            <Button
              colorScheme="gray"
              size="sm"
              mr={2}
              onClick={openFromInvoiceModal}
            >
              Desde factura
            </Button>
            <Button
              colorScheme="gray"
              size="sm"
              onClick={() => {
                setForm({
                  productId: '',
                  shelfLetter: '',
                  shelfNumber: '',
                  zone: '',
                  quantity: 0,
                  expirationDate: '',
                });
                setNewLotReason('');
                onNewLotOpen();
              }}
            >
              + Nuevo lote
            </Button>
          </>
        ) : null
      }
    >
      <Tabs
        index={tabIndex}
        onChange={(index) => {
          setTabIndex(index);
          navigate(`/admin/stock?tab=${STOCK_TAB_QUERY_KEYS[index]}`, { replace: true });
        }}
        variant="enclosed"
        colorScheme="gray"
      >
        <TabList
          flexWrap="wrap"
          bg="gray.50"
          borderBottomWidth="1px"
          borderColor="gray.200"
        >
          <Tab fontSize="sm">Inventario</Tab>
          <Tab fontSize="sm">Historial de movimientos</Tab>
          <Tab fontSize="sm">Controles de stock</Tab>
        </TabList>
      </Tabs>

      <Box pt={4}>
      {tabIndex === 0 && (
      <>
      <Flex mb={4} gap={2} flexWrap="wrap">
      <Input
        size="sm"
        mb={4}
        maxW="400px"
        placeholder="Filtrar por producto, código, zona o repisa…"
        value={locationFilter}
        onChange={(e) => setLocationFilter(e.target.value)}
      />
        <Button
          size="sm"
          variant="outline"
          onClick={() => exportStockExcel(exportData)}
        >
          Exportar inventario
        </Button>
      </Flex>

      {zoneEntries.length === 0 ? (
        <Box py={8} textAlign="center" color="gray.500" fontSize="sm">
          {canEdit
            ? 'No hay lotes registrados. Use “+ Nuevo lote” o “Desde factura”.'
            : 'No hay lotes registrados.'}
        </Box>
      ) : visibleLotCount === 0 ? (
        <Box py={8} textAlign="center" color="gray.500" fontSize="sm">
          Ningún lote coincide con el filtro.
          <Button
            size="xs"
            variant="link"
            ml={2}
            onClick={() => setLocationFilter('')}
          >
            Limpiar filtro
          </Button>
        </Box>
      ) : (
      <Accordion
        allowMultiple
        index={expandedIndices}
        onChange={(idx) => setExpandedIndices(Array.isArray(idx) ? idx : [idx])}
      >
        {zoneEntries.map(([zoneName, lots]) => {
          const filteredLots = lots.filter(filterLot);
          if (filteredLots.length === 0) return null;
          return (
          <AccordionItem key={zoneName}>
            <AccordionButton>
              <Box flex="1" textAlign="left" fontWeight="bold">
                Zona: {zoneName} ({filteredLots.length} lotes)
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel p={0}>
              {Object.entries(
                filteredLots.reduce((acc, lot) => {
                  const key = `${lot.shelfLetter || '-'}|${lot.shelfNumber || '-'}`;
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(lot);
                  return acc;
                }, {}),
              ).map(([shelf, shelfLots]) => (
                <Box key={shelf} mb={4} pl={4}>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Repisa {shelfLots[0]?.shelfLetter || '—'} · Estante{' '}
                    {shelfLots[0]?.shelfNumber || '—'}
                  </Text>
                  <Table size="sm" variant="simple">
                    <Thead bg="gray.100">
                      <Tr>
                        <Th>Lote</Th>
                        <Th>Producto</Th>
                        <Th>Código</Th>
                        <Th isNumeric>Cant.</Th>
                        <Th>Vencimiento</Th>
                        <Th>Último control</Th>
                        {canEdit && <Th>Acciones</Th>}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {shelfLots.map((lot) => (
                        <Tr
                          key={lot.id}
                          bg={
                            lot.id === highlightLotId
                              ? 'green.50'
                              : lot.quantity <= 2
                                ? 'orange.50'
                                : undefined
                          }
                        >
                          <Td fontSize="xs" color="gray.500">
                            #{lot.id}
                          </Td>
                          <Td fontSize="sm">{lot.product?.name}</Td>
                          <Td fontSize="sm" fontFamily="mono">
                            {formatCompactProductCode(lot.product?.code) || '—'}
                          </Td>
                          <Td isNumeric>
                            {lot.quantity}
                            {lot.quantity <= 2 && (
                              <Badge ml={1} colorScheme="orange" fontSize="xs">
                                Bajo
                              </Badge>
                            )}
                          </Td>
                          <Td fontSize="xs">
                            {formatLotExpiration(lot.expirationDate)}
                          </Td>
                          <Td fontSize="xs">
                            {lot.lastCheckUser?.name || '—'}
                            {lot.lastCheckAt && (
                              <Text display="block" color="gray.400">
                                {new Date(lot.lastCheckAt).toLocaleDateString('es-UY')}
                              </Text>
                            )}
                          </Td>
                          {canEdit && (
                            <Td>
                              <Flex gap={1} flexWrap="wrap">
                              <Button
                                size="xs"
                                colorScheme="green"
                                variant="outline"
                                onClick={() => openAction(lot, 'add')}
                                title="Ingresar cantidad"
                              >
                                +
                              </Button>
                              <Button
                                size="xs"
                                colorScheme="red"
                                variant="outline"
                                onClick={() => openAction(lot, 'deduct')}
                                title="Descontar"
                              >
                                −
                              </Button>
                              <Button
                                size="xs"
                                colorScheme="orange"
                                variant="outline"
                                onClick={() => openAction(lot, 'adjust')}
                                title="Ajuste manual"
                              >
                                =
                              </Button>
                              <Button
                                size="xs"
                                variant="ghost"
                                onClick={() => openEditLot(lot)}
                                title="Editar ubicación / vencimiento"
                              >
                                Editar
                              </Button>
                              </Flex>
                            </Td>
                          )}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ))}
            </AccordionPanel>
          </AccordionItem>
          );
        })}
      </Accordion>
      )}
      </>
      )}

      {tabIndex === 1 && (
      <>
            <Input
              size="sm"
              mb={4}
              maxW="400px"
              placeholder="Filtrar movimientos…"
              value={movementFilter}
              onChange={(e) => setMovementFilter(e.target.value)}
            />
            <TableContainer borderWidth="1px" borderRadius="md">
              <Table size="sm">
                <Thead bg="gray.100">
                  <Tr>
                    <Th>Fecha</Th>
                    <Th>Tipo</Th>
                    <Th>Producto</Th>
                    <Th>Lote</Th>
                    <Th isNumeric>Antes</Th>
                    <Th isNumeric>Cambio</Th>
                    <Th isNumeric>Después</Th>
                    <Th>Usuario</Th>
                    <Th>Motivo</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredMovements.length === 0 ? (
                    <Tr>
                      <Td colSpan={9} color="gray.500">
                        Sin movimientos registrados
                      </Td>
                    </Tr>
                  ) : (
                    filteredMovements.map((m) => {
                      const meta = movementLabel(m);
                      return (
                        <Tr
                          key={m.id}
                          bg={m.movementType === 'adjust' ? 'yellow.50' : undefined}
                        >
                          <Td fontSize="xs" whiteSpace="nowrap">
                            {m.createdAt
                              ? new Date(m.createdAt).toLocaleString('es-UY')
                              : '—'}
                          </Td>
                          <Td>
                            <Badge colorScheme={meta.color} fontSize="xs">
                              {meta.label}
                            </Badge>
                          </Td>
                          <Td fontSize="xs">{m.product?.name}</Td>
                          <Td fontSize="xs" color="gray.500">
                            {m.lotId ? `#${m.lotId}` : '—'}
                          </Td>
                          <Td isNumeric>{m.previousQuantity ?? '—'}</Td>
                          <Td
                            isNumeric
                            color={m.quantity < 0 ? 'red.600' : 'green.600'}
                          >
                            {m.quantity > 0 ? `+${m.quantity}` : m.quantity}
                          </Td>
                          <Td isNumeric>{m.newQuantity ?? '—'}</Td>
                          <Td fontSize="xs">{m.user?.name || '—'}</Td>
                          <Td fontSize="xs" maxW="220px">
                            {m.notes}
                            {m.vehicleServiceId ? ` (#${m.vehicleServiceId})` : ''}
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>
            </TableContainer>
      </>
      )}

      {tabIndex === 2 && (
      <Box>
            <Input
              size="sm"
              mb={2}
              maxW="400px"
              placeholder="Filtrar por estado, usuario o #…"
              value={controlFilter}
              onChange={(e) => setControlFilter(e.target.value)}
            />
            <TableContainer borderWidth="1px" borderRadius="md" bg="white">
              <Table size="sm">
                <Thead bg="gray.100">
                  <Tr>
                    <Th>#</Th>
                    <Th>Estado</Th>
                    <Th>Inicio</Th>
                    <Th>Finalización</Th>
                    <Th isNumeric>Progreso</Th>
                    <Th>Usuario</Th>
                    <Th>Acciones</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredControlSessions.length === 0 ? (
                    <Tr>
                      <Td colSpan={7} color="gray.500">
                        Sin controles registrados
                      </Td>
                    </Tr>
                  ) : (
                    filteredControlSessions.map((s) => {
                      const st = sessionStatusLabel(s.status);
                      const prog = s.progress || { checked: 0, total: 0 };
                      const openControl = () =>
                        navigate(stockControlSessionPath(s.id, { viewOnly: !canEdit }));

                      return (
                        <Tr
                          key={s.id}
                          cursor="pointer"
                          _hover={{ bg: 'blue.50' }}
                          onClick={openControl}
                          title="Abrir control de stock"
                        >
                          <Td fontSize="xs">
                            #{s.id}
                          </Td>
                          <Td>
                            <Badge colorScheme={st.color} fontSize="xs">
                              {st.label}
                            </Badge>
                          </Td>
                          <Td fontSize="xs" whiteSpace="nowrap">
                            {s.startedAt
                              ? new Date(s.startedAt).toLocaleString('es-UY')
                              : '—'}
                          </Td>
                          <Td fontSize="xs" whiteSpace="nowrap">
                            {s.finishedAt
                              ? new Date(s.finishedAt).toLocaleString('es-UY')
                              : '—'}
                          </Td>
                          <Td isNumeric fontSize="xs">
                            {prog.checked}/{prog.total}
                          </Td>
                          <Td fontSize="xs">{s.userName || '—'}</Td>
                          <Td
                            onClick={
                              (canEdit && s.status === 'draft') ||
                              s.status === 'finalized'
                                ? (e) => e.stopPropagation()
                                : undefined
                            }
                          >
                            <Flex gap={1} flexWrap="wrap">
                              {canEdit && s.status === 'draft' && (
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={() => handleDiscardSession(s)}
                                >
                                  Descartar
                                </Button>
                              )}
                              {s.status === 'finalized' && (
                                <Button
                                  size="xs"
                                  variant="outline"
                                  onClick={async () => {
                                    try {
                                      const res = await getData(
                                        `/stock/control-sessions/${s.id}`,
                                      );
                                      const snapshot = res?.payload?.session?.snapshot;
                                      if (!snapshot) throw new Error('Sin datos');
                                      exportStockControlSessionExcel(snapshot, {
                                        id: s.id,
                                        date: s.finishedAt
                                          ? new Date(s.finishedAt)
                                              .toISOString()
                                              .slice(0, 10)
                                          : undefined,
                                      });
                                    } catch {
                                      toast({
                                        title: 'No se pudo exportar',
                                        status: 'error',
                                        duration: 3000,
                                      });
                                    }
                                  }}
                                >
                                  Exportar
                                </Button>
                              )}
                            </Flex>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>
            </TableContainer>
      </Box>
      )}
      </Box>

      {/* Modal acciones con motivo */}
      <ReasonModal
        isOpen={!!actionModal}
        onClose={() => {
          setActionModal(null);
          setSelectedLot(null);
        }}
        title={actionTitles[actionModal] || ''}
        onConfirm={runLotAction}
        isLoading={loading}
      >
        {selectedLot && (
          <Box fontSize="sm" mb={2} p={2} bg="gray.50" borderRadius="md">
            <Text>
              <strong>{selectedLot.product?.name}</strong> — Lote #{selectedLot.id}
            </Text>
            <Text fontFamily="mono" fontSize="sm">
              Cód. pieza: {formatCompactProductCode(selectedLot.product?.code) || '—'}
            </Text>
            <Text>Stock actual: {selectedLot.quantity}</Text>
          </Box>
        )}
        <FormLabel fontSize="sm">
          {actionModal === 'add' && 'Cantidad a ingresar'}
          {actionModal === 'deduct' && 'Cantidad a descontar'}
          {actionModal === 'adjust' && 'Nuevo total en stock'}
        </FormLabel>
        <Input
          size="sm"
          type="number"
          min={actionModal === 'adjust' ? 0 : 1}
          value={actionQty}
          onChange={(e) => setActionQty(e.target.value)}
        />
      </ReasonModal>

      {/* Modal nuevo lote */}
      <Modal isOpen={isNewLotOpen} onClose={onNewLotClose} size="lg" closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Nuevo lote de stock</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ProductSearchSelect
              label="Producto (catálogo)"
              value={form.productId}
              onChange={(id) => setForm({ ...form, productId: id })}
            />
            <Flex gap={3} mb={3} mt={3}>
              <Box flex={1}>
                <FormLabel fontSize="sm">Zona</FormLabel>
                <Input
                  size="sm"
                  value={form.zone}
                  onChange={(e) => setForm({ ...form, zone: e.target.value })}
                />
              </Box>
              <Box w="70px">
                <FormLabel fontSize="sm">Repisa</FormLabel>
                <Input
                  size="sm"
                  value={form.shelfLetter}
                  onChange={(e) => setForm({ ...form, shelfLetter: e.target.value })}
                />
              </Box>
              <Box w="90px">
                <FormLabel fontSize="sm">Estante</FormLabel>
                <Input
                  size="sm"
                  value={form.shelfNumber}
                  onChange={(e) => setForm({ ...form, shelfNumber: e.target.value })}
                />
              </Box>
            </Flex>
            <Flex gap={3} mb={3}>
              <Box flex={1}>
                <FormLabel fontSize="sm">Cantidad</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                />
              </Box>
              <Box flex={1}>
                <FormLabel fontSize="sm">Vencimiento</FormLabel>
                <Input
                  size="sm"
                  type="date"
                  value={form.expirationDate}
                  onChange={(e) => setForm({ ...form, expirationDate: e.target.value })}
                />
              </Box>
            </Flex>
            <FormLabel fontSize="sm">
              Motivo del alta <Text as="span" color="red.500">*</Text>
            </FormLabel>
            <Textarea
              size="sm"
              rows={2}
              value={newLotReason}
              onChange={(e) => setNewLotReason(e.target.value)}
              placeholder="Ej: Compra proveedor, devolución..."
            />
          </ModalBody>
          <ModalFooter>
            <Button
              size="sm"
              colorScheme="gray"
              onClick={handleCreateLot}
              isDisabled={!form.productId || newLotReason.trim().length < 3}
            >
              Guardar lote
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal cargar desde factura de compra */}
      <Modal
        isOpen={isFromInvoiceOpen}
        onClose={onFromInvoiceClose}
        size="4xl"
        scrollBehavior="inside"
       closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">
            Cargar lotes desde factura de compra
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormLabel fontSize="sm">Factura de compra</FormLabel>
            <Select
              size="sm"
              mb={4}
              placeholder="Seleccionar factura…"
              value={selectedInvoiceId}
              onChange={(e) => loadInvoiceDetails(e.target.value)}
            >
              {purchaseInvoices.map((inv) => (
                <option key={inv.invoiceId} value={inv.invoiceId}>
                  {inv.invoiceId}
                  {inv.provider?.name ? ` · ${inv.provider.name}` : ''}
                  {inv.date ? ` · ${String(inv.date).slice(0, 10)}` : ''}
                </option>
              ))}
            </Select>

            <Text fontSize="sm" fontWeight="semibold" mb={2}>
              Ubicación por defecto
            </Text>
            <Flex gap={3} mb={4} flexWrap="wrap">
              <Box flex={1} minW="140px">
                <FormLabel fontSize="xs">Zona</FormLabel>
                <Input
                  size="sm"
                  value={invoiceLocation.zone}
                  onChange={(e) =>
                    setInvoiceLocation((prev) => ({ ...prev, zone: e.target.value }))
                  }
                />
              </Box>
              <Box w="80px">
                <FormLabel fontSize="xs">Repisa</FormLabel>
                <Input
                  size="sm"
                  value={invoiceLocation.shelfLetter}
                  onChange={(e) =>
                    setInvoiceLocation((prev) => ({
                      ...prev,
                      shelfLetter: e.target.value,
                    }))
                  }
                />
              </Box>
              <Box w="100px">
                <FormLabel fontSize="xs">Estante</FormLabel>
                <Input
                  size="sm"
                  value={invoiceLocation.shelfNumber}
                  onChange={(e) =>
                    setInvoiceLocation((prev) => ({
                      ...prev,
                      shelfNumber: e.target.value,
                    }))
                  }
                />
              </Box>
              <Box flex={1} minW="140px">
                <FormLabel fontSize="xs">Vencimiento</FormLabel>
                <Input
                  size="sm"
                  type="date"
                  value={invoiceLocation.expirationDate}
                  onChange={(e) =>
                    setInvoiceLocation((prev) => ({
                      ...prev,
                      expirationDate: e.target.value,
                    }))
                  }
                />
              </Box>
            </Flex>

            {loadingInvoice ? (
              <Flex justify="center" py={8}>
                <Spinner />
              </Flex>
            ) : selectedInvoiceId && invoiceLines.length === 0 ? (
              <Text fontSize="sm" color="gray.500" py={4}>
                Esta factura no tiene productos.
              </Text>
            ) : invoiceLines.length > 0 ? (
              <VStack align="stretch" spacing={3}>
                <Text fontSize="sm" fontWeight="semibold">
                  Productos de la factura
                </Text>
                <TableContainer maxH="360px" overflowY="auto" borderWidth="1px" borderRadius="md">
                  <Table size="sm">
                    <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th w="40px"></Th>
                        <Th>N° pieza</Th>
                        <Th>Producto</Th>
                        <Th w="110px">Cantidad</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {invoiceLines.map((line, index) => (
                        <Tr key={line.detailId || index}>
                          <Td>
                            <Checkbox
                              isChecked={line.included}
                              isDisabled={!line.productId}
                              onChange={(e) =>
                                setInvoiceLines((prev) =>
                                  prev.map((l, i) =>
                                    i === index
                                      ? { ...l, included: e.target.checked }
                                      : l,
                                  ),
                                )
                              }
                            />
                          </Td>
                          <Td fontSize="xs">
                            {formatCompactProductCode(line.productCode) || '—'}
                          </Td>
                          <Td fontSize="sm">{line.productName}</Td>
                          <Td>
                            <Input
                              size="xs"
                              type="number"
                              min={0}
                              value={line.quantity}
                              isDisabled={!line.included}
                              onChange={(e) =>
                                setInvoiceLines((prev) =>
                                  prev.map((l, i) =>
                                    i === index
                                      ? { ...l, quantity: e.target.value }
                                      : l,
                                  ),
                                )
                              }
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
                <Checkbox
                  size="sm"
                  isChecked={forceReloadInvoice}
                  onChange={(e) => setForceReloadInvoice(e.target.checked)}
                >
                  Forzar carga aunque la factura ya haya ingresado stock
                </Checkbox>
              </VStack>
            ) : (
              <Text fontSize="sm" color="gray.500">
                Seleccioná una factura para ver sus productos y cantidades.
              </Text>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={onFromInvoiceClose}>
              Cancelar
            </Button>
            <Button
              size="sm"
              colorScheme="gray"
              onClick={handleCreateLotsFromInvoice}
              isLoading={savingFromInvoice}
              loadingText="Cargando..."
              isDisabled={!selectedInvoiceId || invoiceLines.every((l) => !l.included)}
            >
              Cargar lotes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Editar lote (ubicación / vencimiento) */}
      <Modal
        isOpen={!!editLot}
        onClose={() => setEditLot(null)}
        size="lg"
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Editar lote #{editLot?.id}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={3} p={2} bg="gray.50" borderRadius="md">
              <strong>{editLot?.product?.name}</strong>
              <br />
              Cód. pieza:{' '}
              {formatCompactProductCode(editLot?.product?.code) || '—'}
              <br />
              Cantidad actual: {editLot?.quantity}
            </Text>
            <Flex gap={3} mb={3}>
              <Box flex={1}>
                <FormLabel fontSize="sm">Zona (depósito)</FormLabel>
                <Input
                  size="sm"
                  value={editForm.zone}
                  onChange={(e) => setEditForm({ ...editForm, zone: e.target.value })}
                />
              </Box>
              <Box w="70px">
                <FormLabel fontSize="sm">Repisa</FormLabel>
                <Input
                  size="sm"
                  value={editForm.shelfLetter}
                  onChange={(e) =>
                    setEditForm({ ...editForm, shelfLetter: e.target.value })
                  }
                />
              </Box>
              <Box w="90px">
                <FormLabel fontSize="sm">Estante</FormLabel>
                <Input
                  size="sm"
                  value={editForm.shelfNumber}
                  onChange={(e) =>
                    setEditForm({ ...editForm, shelfNumber: e.target.value })
                  }
                />
              </Box>
            </Flex>
            <FormLabel fontSize="sm">Vencimiento del lote</FormLabel>
            <Input
              size="sm"
              type="date"
              mb={3}
              value={editForm.expirationDate}
              onChange={(e) =>
                setEditForm({ ...editForm, expirationDate: e.target.value })
              }
            />
            <FormLabel fontSize="sm">
              Motivo de la edición <Text as="span" color="red.500">*</Text>
            </FormLabel>
            <Textarea
              size="sm"
              rows={2}
              value={editForm.reason}
              onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
              placeholder="Obligatorio. Queda en el historial de movimientos."
            />
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" mr={2} onClick={() => setEditLot(null)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              colorScheme="orange"
              onClick={confirmEditLot}
              isLoading={loading}
              isDisabled={editForm.reason.trim().length < 3}
            >
              Guardar cambios
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={!!discardTarget}
        onClose={() => !discarding && setDiscardTarget(null)}
        onConfirm={confirmDiscardSession}
        header="Descartar borrador"
        message={
          <Text fontSize="sm" color="gray.700">
            ¿Descartar el borrador{' '}
            <Text as="span" fontWeight="bold">
              #{discardTarget?.id}
            </Text>
            ? No se podrá recuperar.
          </Text>
        }
        confirmLabel="Descartar"
        confirmVariant="danger"
        isLoading={discarding}
      />

    </AdminPageShell>
  );
}
